import React, { Fragment } from 'react';
import { IconStoreLight } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { Typography } from '@common/styles';
import {
  BadgeMileageImage,
  GetPdpBaseInfo_catalog_product_base_info_brand_home,
  GetPdpBaseInfo_catalog_product_base_info_shop,
} from '@domains/pdp-common';

import { BookmarkContainer } from '../containers/BookmarkContainer';
import { GetPdpShopStory_has_story } from '../graphql/shop-story_types.2';

interface ShopProps {
  className?: string;
  targetRef?: (instance: Element | null) => void;
  is_app: boolean | null;
  isMileageShop: boolean;
  has_story: GetPdpShopStory_has_story | null;
  item: GetPdpBaseInfo_catalog_product_base_info_brand_home | GetPdpBaseInfo_catalog_product_base_info_shop;
  category_list: string[] | null;
  show_placeholder_image: boolean;
  goToShopDetail: () => void;
  goToStory: (e: React.MouseEvent<HTMLElement>) => void;
  onImageError: () => void;
}

export const Shop: React.FC<React.PropsWithChildren<ShopProps>> = ({
  className,
  targetRef,
  is_app,
  isMileageShop,
  has_story,
  item,
  category_list,
  show_placeholder_image,
  goToShopDetail,
  goToStory,
  onImageError,
}) => {
  return (
    <div className={className}>
      <Container onClick={goToShopDetail}>
        <Base>
          <Story onClick={goToStory} has_story={has_story?.has_story}>
            <StoryBorder is_app={!!is_app} has_story={has_story?.has_story} is_new={has_story?.is_new}>
              <ImageWrap ref={targetRef}>
                {!show_placeholder_image && item.typical_image_url && (
                  <TypicalImage src={item.typical_image_url} alt='' onError={onImageError} />
                )}
                {(!item.typical_image_url || show_placeholder_image) && (
                  <PlaceholderImage>
                    <IconStoreLight v3Color={'gray200'} />
                  </PlaceholderImage>
                )}
              </ImageWrap>
            </StoryBorder>
          </Story>
          <div>
            <Flex>
              <ShopName className={Typography.BODY_16_SEMIBOLD}>{item.name}</ShopName>
              {isMileageShop && <BadgeMileageImage className='badge' height={16} />}
            </Flex>
            {category_list && (
              <ShopCategory>
                {category_list.map((category, i) => {
                  return (
                    <Fragment key={`${category}-${i}`}>
                      {i > 0 && <Divider />}
                      <span className={Typography.CAPTION_12_SEMIBOLD}>{category}</span>
                    </Fragment>
                  );
                })}
              </ShopCategory>
            )}
          </div>
        </Base>
        {is_app && <BookmarkContainer />}
      </Container>
    </div>
  );
};

const Container = styled.div`
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 72px;
  padding: 0 20px 0 0;
`;

const Base = styled.div`
  display: flex;
  align-items: center;
  width: calc(100% - 48px);
  height: 100%;
`;

const Story = styled.div<{ has_story?: boolean }>`
  padding: ${({ has_story }) => (has_story ? '0 8px 0 14px' : '0 12px 0 16px')};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StoryBorder = styled.div<{ is_app: boolean; has_story?: boolean; is_new?: boolean }>`
  ${({ is_app, has_story, is_new }) =>
    is_app &&
    has_story &&
    `
    padding: 2px;
    border-radius: 50%;
    border: 2px solid ${is_new ? vars.v3Color.pink400 : vars.v3Color.gray200}; 
  `};
`;

const ImageWrap = styled.div`
  display: block;
  width: 48px;
  height: 48px;
  border-radius: 100px;
  border: 1px solid ${vars.v3Color.gray150};
  background-color: ${vars.v3Color.gray100};
  overflow: hidden;
`;

const TypicalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ShopName = styled.h2`
  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: keep-all;
  color: ${vars.v3Color.gray900};
`;

const ShopCategory = styled.div`
  display: flex;
  align-items: center;
  margin: 4px 0 0;
  color: ${vars.color.textTertiary};
`;

const Divider = styled.div`
  width: 2px;
  height: 2px;
  margin: 0 4px;
  background: ${vars.color.textQuaternary};
`;

const PlaceholderImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;

  .badge {
    margin: 0 0 0 2px;
  }
`;
